/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Box, Button, FormControl, Autocomplete, TextField, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  LOAD_CLIENTS,
  loadAllClients
} from 'src/store/actions/clientActions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { selectClientsList } from 'src/store/selectors/clientSelectors';
import { hasAddressCompleted } from 'src/utils/common';
import { AddressType } from 'src/utils/constants/address';
import { selectAddressByType } from 'src/utils/address';
import useStyles from '../styles';
import validationSchema from './schema';
import makeInitialState from './initialState';

const StepTwo = ({
  data,
  handleNext,
  isQuoteAssignedToClient,
  open,
  resetRef,
  handleBack
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const initialState = useMemo(() => makeInitialState(data), [data]);
  const { t } = useTranslation();
  const loadingClients = useSelector(isLoadingSelector([LOAD_CLIENTS.action]));

  const clientList = useSelector(selectClientsList);

  useEffect(() => {
    if (open) {
      dispatch(loadAllClients());
    }
  }, [open, dispatch]);


  const onNext = useCallback(
    values => {
      handleNext(values);
    },
    [handleNext]
  );


  return (
    <Formik
      initialValues={initialState}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onNext}
    >
      {({
        errors,
        handleSubmit,
        touched,
        values,
        setFieldValue,
        setFieldError,
        setFieldTouched,
        resetForm
      }) => {
        resetRef.current = () => {
          resetForm();
        };

        return (
          <form onSubmit={handleSubmit}>
            <Box className={classes.container}>
              <FormControl
                fullWidth
                error={Boolean(touched.clientId && errors.clientId)}
              >
                <Tooltip title={isQuoteAssignedToClient ? t('COMMON.CLIENT_SELECTED') : ''}>
                  <Autocomplete
                    options={clientList}
                    id="clientId"
                    loading={loadingClients}
                    disabled={loadingClients || isQuoteAssignedToClient}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id || value === ''
                    }
                    getOptionLabel={option => {
                      return option.fullName
                        ? `${option.fullName} - ID:${option.id}`
                        : '';
                    }}
                    value={
                      clientList.find(
                        client => client.id === values.clientId
                      ) ?? null
                    }
                    fullWidth
                    clearIcon={null}
                    noOptionsText={t('COMMON.NO_OPTIONS')}
                    sx={{ marginBottom: 2, marginTop: 2 }}
                    onChange={(_e, value) => {
                      if (!value) {
                        setFieldValue('clientId', '');
                        setFieldValue('clientPersonType', null);
                        return;
                      }

                      setFieldValue('clientId', value.id);
                      setFieldValue(
                        'clientPersonType',
                        value.personType ?? null
                      );
                      setFieldError('clientId', undefined);
                      setFieldTouched('clientId', false);
                      const address =
                        selectAddressByType(
                          value.addresses,
                          AddressType.WORK
                        ) || {};
                      if (hasAddressCompleted(address)) {
                        setFieldValue('isExistingAddress', true);
                        setFieldValue('equipmentLocation', {
                          street: address.street,
                          insideHomeNumber: address.insideHomeNumber,
                          externalHomeNumber: address.externalHomeNumber,
                          colonia: address.colonia,
                          city: address.city,
                          state: address.state,
                          municipality: address.municipality,
                          zipCode: address.zipCode
                        });
                      }
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          autoComplete: 'off'
                        }}
                        label={loadingClients ? `${t('COMMON.LOADING')} ${t('COMMON.CLIENT')}` : t('COMMON.CLIENT')}
                        error={Boolean(touched.clientId && errors.clientId)}
                        helperText={touched.clientId && t(errors.clientId)}
                        disabled={loadingClients || isQuoteAssignedToClient}
                      />
                    )}
                  />
                </Tooltip>
              </FormControl>
            </Box>
            <Box
              className={classes.box}
              display="flex"
              justifyContent="space-between"
            >
              <Button
                className={classes.buttons}
                onClick={() => {
                  handleBack();
                }}
                color="primary"
                variant="outlined"
              >
                {t('COMMON.BACK')}
              </Button>
              <Button
                className={clsx(classes.buttons, classes.buttonMain)}
                type="submit"
                color="primary"
                variant="outlined"
                disabled={loadingClients}
              >
                {t('COMMON.NEXT')}
              </Button>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};
StepTwo.propTypes = {
  open: PropTypes.bool,
  data: PropTypes.object,
  resetRef: PropTypes.object,
  handleNext: PropTypes.func,
  handleBack: PropTypes.func,
  isQuoteAssignedToClient: PropTypes.bool
};

export default StepTwo;
